// src/components/common/PaymentOptions.tsx
import React, { useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SplitInstallment {
  /** Amount in kobo. */
  amount: number;
  /** Human-readable due label, e.g. "in 30 days" */
  dueLabel: string;
}

export interface PaymentOptionsProps {
  fullPrice: number;
  splitFirst: number;
  splitInstallments?: SplitInstallment[];
  /** @deprecated Use splitInstallments instead */
  splitSecond?: number;
  labels?: { full?: string; split?: string };
  features?: string[];
  initialSelected?: "full" | "split";
  onSelect?: (plan: "full" | "split") => void;
  onSubmit: (payload: {
    name: string;
    email: string;
    phone: string;
    paymentPlan: "full" | "split";
    amount: number;
  }) => void | Promise<void>;
  /**
   * When true, the submit button shows a spinner and is disabled.
   * Pass your page-level `isSubmitting` state here so the parent controls
   * the loading state (e.g. while awaiting the Paystack initialize API call).
   * If omitted, PaymentOptions manages its own internal submitting state.
   */
  isSubmitting?: boolean;
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number): string =>
  "₦" + (n / 100).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const CheckCircle: React.FC = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
    <span className="text-green-600 text-xs font-bold">✓</span>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

export const PaymentOptions: React.FC<PaymentOptionsProps> = ({
  fullPrice,
  splitFirst,
  splitInstallments,
  splitSecond,
  labels,
  features = [],
  initialSelected = "full",
  onSelect,
  onSubmit,
  isSubmitting: isSubmittingProp,
  className = "",
}) => {
  const [selected, setSelected] = useState<"full" | "split">(initialSelected);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The parent can control the busy state via isSubmittingProp.
  // If the prop is not provided, fall back to internal state.
  const isControlled = isSubmittingProp !== undefined;
  const submitting = isControlled ? isSubmittingProp : internalSubmitting;

  // Resolve future installments
  const resolvedInstallments: SplitInstallment[] = (() => {
    if (splitInstallments && splitInstallments.length > 0)
      return splitInstallments;
    const second =
      splitSecond !== undefined
        ? splitSecond
        : Math.max(0, fullPrice - splitFirst);
    return [{ amount: second, dueLabel: "in 30 days" }];
  })();

  const totalInstallments = 1 + resolvedInstallments.length;
  const splitTotal =
    splitFirst + resolvedInstallments.reduce((s, i) => s + i.amount, 0);
  const amountToday = selected === "full" ? fullPrice : splitFirst;

  const handleSelect = (plan: "full" | "split") => {
    setSelected(plan);
    onSelect?.(plan);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
    if (errors[e.target.name])
      setErrors((s) => ({ ...s, [e.target.name]: "" }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "A valid email address is required";
    if (!form.phone.trim()) errs.phone = "WhatsApp number is required";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    if (!isControlled) setInternalSubmitting(true);
    try {
      await Promise.resolve(
        onSubmit({ ...form, paymentPlan: selected, amount: amountToday }),
      );
    } catch (err) {
      console.error("PaymentOptions onSubmit error:", err);
    } finally {
      if (!isControlled) setInternalSubmitting(false);
    }
  };

  return (
    <div className={`payment-options ${className}`}>
      {/* ── Plan selection cards ─────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Full Payment Card */}
        <button
          type="button"
          onClick={() => handleSelect("full")}
          className={`text-left w-full rounded-2xl p-7 border-2 transition-all duration-200 focus:outline-none ${
            selected === "full"
              ? "border-blue-600 shadow-2xl ring-4 ring-blue-100 scale-[1.02]"
              : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
          } bg-white`}
        >
          <div className="flex items-center justify-between mb-4">
            <span
              className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest ${
                selected === "full"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {selected === "full" ? "✓ Selected" : "Pay Once"}
            </span>
            <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full">
              Best Value
            </span>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {labels?.full ?? "Full Payment"}
          </h3>
          <p className="text-sm text-gray-500 mb-5">
            One payment. Lowest total cost. Starts immediately.
          </p>

          <div className="mb-5">
            <span className="text-4xl font-extrabold text-gray-900">
              {fmt(fullPrice)}
            </span>
            <span className="text-gray-500 text-sm ml-2">one-time</span>
          </div>

          <ul className="space-y-2">
            {features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <CheckCircle />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </button>

        {/* Split Payment Card */}
        <button
          type="button"
          onClick={() => handleSelect("split")}
          className={`text-left w-full rounded-2xl p-7 border-2 transition-all duration-200 focus:outline-none ${
            selected === "split"
              ? "border-blue-600 shadow-2xl ring-4 ring-blue-100 scale-[1.02]"
              : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
          } bg-white`}
        >
          <div className="flex items-center justify-between mb-4">
            <span
              className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest ${
                selected === "split"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {selected === "split"
                ? "✓ Selected"
                : `${totalInstallments}-Part Plan`}
            </span>
            <span className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
              Flexible
            </span>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {labels?.split ?? "Split Payment"}
          </h3>
          <p className="text-sm text-gray-500 mb-5">
            Start today, spread the balance over {resolvedInstallments.length}{" "}
            month{resolvedInstallments.length > 1 ? "s" : ""}.
          </p>

          <div className="mb-2">
            <span className="text-4xl font-extrabold text-gray-900">
              {fmt(splitFirst)}
            </span>
            <span className="text-gray-500 text-sm ml-2">due today</span>
          </div>

          {/* Payment schedule */}
          <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Payment Schedule
            </p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                Today (1 of {totalInstallments})
              </span>
              <span className="font-bold text-gray-900">{fmt(splitFirst)}</span>
            </div>
            {resolvedInstallments.map((inst, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Payment {i + 2} of {totalInstallments}{" "}
                  <span className="italic text-gray-400">
                    ({inst.dueLabel})
                  </span>
                </span>
                <span className="font-bold text-gray-900">
                  {fmt(inst.amount)}
                </span>
              </div>
            ))}
            <div className="pt-2 border-t border-gray-200 flex justify-between text-sm">
              <span className="font-semibold text-gray-700">Total</span>
              <span className="font-extrabold text-gray-900">
                {fmt(splitTotal)}
              </span>
            </div>
          </div>

          <ul className="space-y-2">
            {features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <CheckCircle />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </button>
      </div>

      {/* ── Enrollment form ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-xl p-7 sm:p-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Your Details
        </h3>
        <p className="text-center text-gray-500 text-sm mb-8">
          Fill in your details below to proceed to secure checkout.
        </p>

        {/* Order summary strip */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">
                Selected Plan
              </p>
              <p className="text-lg font-bold text-gray-900">
                {selected === "full"
                  ? (labels?.full ?? "Full Payment")
                  : (labels?.split ?? `Split Payment (${totalInstallments}×)`)}
              </p>
              {selected === "split" && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Remaining:{" "}
                  {resolvedInstallments
                    .map((i) => `${fmt(i.amount)} ${i.dueLabel}`)
                    .join(" · ")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">
                {selected === "full" ? "Total" : "Due Today"}
              </p>
              <p className="text-3xl font-extrabold text-gray-900">
                {fmt(amountToday)}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* Full name */}
          <div>
            <label
              htmlFor="po-name"
              className="block text-sm font-semibold text-gray-800 mb-1.5"
            >
              Full Name <span className="text-brandRed">*</span>
            </label>
            <input
              type="text"
              id="po-name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={submitting}
              className={`w-full px-4 py-3 rounded-xl border-2 text-gray-900 placeholder-gray-400 text-sm transition-colors focus:outline-none focus:border-blue-500 disabled:opacity-60 ${
                errors.name
                  ? "border-red-400 bg-red-50"
                  : "border-gray-200 bg-gray-50"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="po-email"
              className="block text-sm font-semibold text-gray-800 mb-1.5"
            >
              Email Address <span className="text-brandRed">*</span>
            </label>
            <input
              type="email"
              id="po-email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              disabled={submitting}
              className={`w-full px-4 py-3 rounded-xl border-2 text-gray-900 placeholder-gray-400 text-sm transition-colors focus:outline-none focus:border-blue-500 disabled:opacity-60 ${
                errors.email
                  ? "border-red-400 bg-red-50"
                  : "border-gray-200 bg-gray-50"
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* WhatsApp */}
          <div>
            <label
              htmlFor="po-phone"
              className="block text-sm font-semibold text-gray-800 mb-1.5"
            >
              WhatsApp Number <span className="text-brandRed">*</span>
            </label>
            <input
              type="tel"
              id="po-phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+234 xxx xxx xxxx"
              disabled={submitting}
              className={`w-full px-4 py-3 rounded-xl border-2 text-gray-900 placeholder-gray-400 text-sm transition-colors focus:outline-none focus:border-blue-500 disabled:opacity-60 ${
                errors.phone
                  ? "border-red-400 bg-red-50"
                  : "border-gray-200 bg-gray-50"
              }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Security note */}
          <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
            <span className="text-green-600 mt-0.5 text-lg flex-shrink-0">
              🔒
            </span>
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-800">
                Secure checkout via Paystack.
              </span>{" "}
              You'll be redirected to complete payment. Your details are
              encrypted and never stored by us.
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-4 rounded-full font-bold text-base shadow-lg transition-all duration-200 ${
              submitting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-brandRed hover:bg-brandRedHover text-white hover:shadow-xl active:scale-95"
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Redirecting to Paystack…
              </span>
            ) : (
              `Pay ${fmt(amountToday)} — Proceed to Paystack →`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentOptions;